/* ==========================================================
 * Normalization helpers
 * ==========================================================
 */

import { IBieOnboardingImportRecord } from './bieonboarding.import.models';
import { BieRecordId } from './bieonboardingworkbench.models';

export const readValue = (
  source: Record<string, unknown>,
  keys: string[]
): unknown => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }

    const matchedKey = Object.keys(source).find(
      (candidate) => candidate.toLowerCase() === key.toLowerCase()
    );

    if (matchedKey) {
      return source[matchedKey];
    }
  }

  return undefined;
};

export const requireRecordId = (value: unknown): BieRecordId => {
  const normalized = String(value ?? '').trim();

  if (!normalized || !/^\d+$/.test(normalized)) {
    throw new Error('The Import Item response contains an invalid record ID.');
  }

  const numericValue = Number(normalized);

  return Number.isSafeInteger(numericValue) ? numericValue : normalized;
};

export const readId = (value: unknown): BieRecordId | null => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const normalized = String(value).trim();

  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  const numericValue = Number(normalized);

  return Number.isSafeInteger(numericValue) ? numericValue : normalized;
};

export const readText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

export const readNullableText = (value: unknown): string | null => {
  const normalized = readText(value);

  return normalized || null;
};

export const readNumber = (value: unknown): number => {
  const normalized = Number(value);

  return Number.isFinite(normalized) ? normalized : 0;
};

export const readBoolean = (value: unknown): boolean => {
  if (value === true || value === 1 || value === '1') {
    return true;
  }

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  return (
    normalized === 'true' || normalized === 'yes' || normalized === 'resolved'
  );
};

export const readObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);

      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }

  return {};
};

export const readSourceValue = (value: unknown): string | number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  const normalized = String(value).trim();

  const numericValue = Number(normalized);

  return Number.isFinite(numericValue) ? numericValue : normalized;
};

/* ==========================================================
 * Record normalization
 *
 * Supports both long descriptive field names and abbreviated
 * vw_ field aliases.
 * ==========================================================
 */

export const normalizeImportRecord = (
  source: Record<string, unknown>
): IBieOnboardingImportRecord => {
  const read = (...keys: string[]): unknown => readValue(source, keys);

  const recordId = requireRecordId(read('rid', 'recid', 'id'));

  const resolutionStatus = readText(
    read('resolutionstatus', 'resolutionStatus', 'rst')
  ).toUpperCase();

  const explicitResolved = read('isresolved', 'isResolved', 'irs');

  const isResolved =
    explicitResolved !== undefined && explicitResolved !== null
      ? readBoolean(explicitResolved)
      : resolutionStatus === 'RESOLVED';

  return {
    rid: recordId,

    rix: readText(read('rix', 'recix')),

    importId: readId(read('importid', 'importId', 'imi')) || '',
    //||this.props.importId,

    importIx: readText(read('importix', 'importIx', 'imx')),

    importCode: readText(read('importcode', 'importCode', 'imc')),

    importName: readText(read('importname', 'importName', 'imn')),

    loadSessionId: readId(read('loadsessionid', 'loadSessionId', 'lsi')),

    loadSessionCode: readText(
      read('loadsessioncode', 'loadSessionCode', 'lsc')
    ),

    loadSessionName: readText(
      read('loadsessionname', 'loadSessionName', 'lsn')
    ),

    classCode: readText(read('classcode', 'classCode', 'clc')).toUpperCase(),

    className: readText(read('classname', 'className', 'cln')),

    recordClassId: readId(read('recordclassid', 'recordClassId', 'rci')),

    recordClassCode: readText(
      read('recordclasscode', 'recordClassCode', 'rcc')
    ).toUpperCase(),

    recordClassName: readText(
      read('recordclassname', 'recordClassName', 'rcn')
    ),

    sourceSheet: readText(read('sourcesheet', 'sourceSheet', 'ssh')),

    sourceRow: readNumber(read('sourcerow', 'sourceRow', 'srw')),

    sourceReference: readText(
      read('sourcereference', 'sourceReference', 'srf')
    ),

    validationStateId: readId(
      read('validationstateid', 'validationStateId', 'vsi')
    ),

    validationCode: readText(
      read('validationcode', 'validationCode', 'vsc', 'vlc')
    ).toUpperCase(),

    validationName: readText(
      read('validationname', 'validationName', 'vsn', 'vln')
    ),

    resolutionStatus:
      resolutionStatus || (isResolved ? 'RESOLVED' : 'UNRESOLVED'),

    isResolved,

    driverId: readId(read('driverid', 'driverId', 'dri')),

    driverCode: readText(
      read('drivercode', 'driverCode', 'drc', 'metriccode', 'metricCode')
    ),

    driverName: readText(
      read('drivername', 'driverName', 'drn', 'metricname', 'metricName')
    ),

    productId: readId(read('productid', 'productId', 'pdi')),

    productCode: readText(read('productcode', 'productCode', 'pdc')),

    productName: readText(read('productname', 'productName', 'pdn')),

    entityId: readId(read('entityid', 'entityId', 'eni')),

    entityCode: readText(read('entitycode', 'entityCode', 'enc')),

    entityName: readText(read('entityname', 'entityName', 'enn')),

    locationId: readId(read('locationid', 'locationId', 'lci')),

    locationCode: readText(read('locationcode', 'locationCode', 'lcc')),

    locationName: readText(read('locationname', 'locationName', 'lcn')),

    accountId: readId(read('accountid', 'accountId', 'aci')),

    accountCode: readText(read('accountcode', 'accountCode', 'acc')),

    accountName: readText(read('accountname', 'accountName', 'acn')),

    periodId: readId(read('periodid', 'periodId', 'pei')),

    periodCode: readText(read('periodcode', 'periodCode', 'pec')),

    periodDate: readNullableText(read('perioddate', 'periodDate', 'ped')),

    unitId: readId(read('unitid', 'unitId', 'uni')),

    unitCode: readText(read('unitcode', 'unitCode', 'unc')),

    unitName: readText(read('unitname', 'unitName', 'unn')),

    sourceValue: readSourceValue(read('sourcevalue', 'sourceValue', 'val')),

    issueCount: readNumber(read('issuecount', 'issueCount', 'isc')),

    blockingIssueCount: readNumber(
      read('blockingissuecount', 'blockingIssueCount', 'bic')
    ),

    warningIssueCount: readNumber(
      read('warningissuecount', 'warningIssueCount', 'wic')
    ),

    rawData: readObject(read('rawdata', 'rawData', 'rwd')),

    validationDetails: readObject(
      read('validationdetails', 'validationDetails', 'vdd')
    ),

    extraInfo: readObject(read('extrainfo', 'extraInfo', 'exi')),

    status: readNumber(read('status', 'sts')),

    stamp: readText(read('stamp', 'stp')),

    source,
  };
};
